import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../components/ui/tabs";
import { Mail, Lock, User, ChevronRight, AlertCircle } from "lucide-react";
import { LanguageToggle } from "../components/LanguageToggle";
import { toast } from "sonner";
import { fetchAPI } from "../lib/utils";
import { consumePendingAuthorization } from "../lib/mcp";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Google Client ID from environment or hardcoded for deployment
const GOOGLE_CLIENT_ID =
	"444440687616-gosrgthajj627uhvp67hqs88qg90degs.apps.googleusercontent.com";

/**
 * L'accesso con Google fallisce con `origin_mismatch` quando la pagina non e'
 * servita da un'origine registrata fra le "Authorized JavaScript origins" del
 * client OAuth. Succede in un caso concreto e ricorrente: un'icona aggiunta
 * alla schermata home **quando l'app stava su un altro indirizzo** (un dominio
 * Vercel di progetto o un'anteprima di deploy). La PWA continua ad aprirsi da
 * quell'origine, e Google mostra una pagina d'errore che non dice cosa fare.
 *
 * Qui l'origine sbagliata si riconosce prima, e si dice all'utente dove andare.
 * `REACT_APP_CANONICAL_ORIGIN` non impostata = nessun controllo (sviluppo,
 * anteprime, chi lavora in locale).
 */
const CANONICAL_ORIGIN = process.env.REACT_APP_CANONICAL_ORIGIN || "";

function getWrongOrigin() {
	if (!CANONICAL_ORIGIN || typeof window === "undefined") return null;
	const current = window.location.origin;
	if (current === CANONICAL_ORIGIN) return null;
	// Il lavoro in locale non va mai ostacolato.
	if (/^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|$)/.test(current))
		return null;
	return current;
}

export default function Login() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { login, register, loginWithGoogleCredential } = useAuth();
	const { t } = useLanguage();
	const [isLoading, setIsLoading] = useState(false);
	const [forgotLoading, setForgotLoading] = useState(false);
	const [error, setError] = useState("");
	const [registerError, setRegisterError] = useState("");
	const [registerFieldErrors, setRegisterFieldErrors] = useState({
		email: false,
		confirmEmail: false,
		password: false,
		confirmPassword: false,
	});
	const [sessionExpired, setSessionExpired] = useState(false);
	const [activeTab, setActiveTab] = useState(
		searchParams.get("mode") === "register" ? "register" : "login",
	);
	const inviteTokenFromQuery = (searchParams.get("invite_token") || "").trim();

	// Check for session expiry redirect
	useEffect(() => {
		document.documentElement.classList.add("login-auth-lock");
		document.body.classList.add("login-auth-lock");
		const rootElement = document.getElementById("root");
		rootElement?.classList.add("login-auth-lock");

		return () => {
			document.documentElement.classList.remove("login-auth-lock");
			document.body.classList.remove("login-auth-lock");
			rootElement?.classList.remove("login-auth-lock");
		};
	}, []);

	useEffect(() => {
		if (searchParams.get("session_expired") === "true") {
			setSessionExpired(true);
			toast.error(t("sessionExpired"));
			// Clean up URL
			window.history.replaceState({}, "", "/login");
		}
	}, [searchParams, t]);

	useEffect(() => {
		if (!inviteTokenFromQuery) return;
		localStorage.setItem("pending_invite_token", inviteTokenFromQuery);
	}, [inviteTokenFromQuery]);

	const [loginData, setLoginData] = useState({ email: "", password: "" });
	const [registerData, setRegisterData] = useState({
		name: "",
		surname: "",
		email: "",
		confirmEmail: "",
		password: "",
		confirmPassword: "",
	});

	const isDataCloneNavigationError = (err) => {
		if (!err) return false;
		const name = String(err.name || "");
		const message = String(err.message || "").toLowerCase();
		if (name === "DataCloneError") return true;
		return (
			message.includes("could not be cloned") ||
			message.includes("cannot be cloned") ||
			message.includes("can not be cloned") ||
			message.includes("performanceservertiming")
		);
	};

	const navigateToDashboardSafe = () => {
		// Un consenso MCP interrotto dal login va ripreso da dove era: mandare
		// in dashboard perderebbe la richiesta del client esterno.
		const pendingMcp = consumePendingAuthorization();
		if (pendingMcp) {
			window.location.assign(pendingMcp);
			return;
		}
		try {
			navigate("/dashboard");
		} catch (navError) {
			if (!isDataCloneNavigationError(navError)) {
				throw navError;
			}
			// Fallback for intermittent mobile/PWA history clone issue.
			toast.error(t("loginNavigationCloneFallback"));
			window.location.assign("/dashboard");
		}
	};

	const navigateToProfileSetupSafe = () => {
		try {
			navigate("/profile?onboarding=complete-profile");
		} catch (navError) {
			if (!isDataCloneNavigationError(navError)) {
				throw navError;
			}
			window.location.assign("/profile?onboarding=complete-profile");
		}
	};

	const shouldCompleteProfile = (authUser) => {
		if (!authUser) return false;
		const role = String(authUser.role || "").toLowerCase();
		if (role && role !== "athlete") return false;
		const hasGender = !!String(authUser.gender || "").trim();
		const hasSport = !!String(authUser.sport || "").trim();
		return !hasGender || !hasSport;
	};

	const applyPendingInviteIfAny = async () => {
		const pendingToken = (
			localStorage.getItem("pending_invite_token") || ""
		).trim();
		if (!pendingToken) return;

		try {
			await fetchAPI(`/invites/${encodeURIComponent(pendingToken)}/accept`, {
				method: "POST",
			});
			localStorage.removeItem("pending_invite_token");
			toast.success(t("inviteAcceptedSuccess"));
		} catch (inviteError) {
			// If token is expired (410), clear it to avoid re-using stale token on next login
			if (inviteError.message && inviteError.message.includes("scaduto")) {
				localStorage.removeItem("pending_invite_token");
			}
			toast.error(inviteError.message || t("inviteAcceptFailed"));
		}
	};

	const handleForgotPassword = async () => {
		const email = loginData.email.trim();
		if (!email) {
			toast.error(t("forgotPasswordEnterEmail"));
			return;
		}
		setForgotLoading(true);
		try {
			const res = await fetchAPI("/auth/forgot-password", {
				method: "POST",
				body: JSON.stringify({ email }),
			});
			toast.success(res?.message || t("forgotPasswordSent"));
		} catch (err) {
			toast.error(err?.message || t("genericError"));
		} finally {
			setForgotLoading(false);
		}
	};

	const handleLogin = async (e) => {
		e.preventDefault();
		setError("");
		setIsLoading(true);
		try {
			await login(loginData.email, loginData.password);
			await applyPendingInviteIfAny();
			navigateToDashboardSafe();
		} catch (err) {
			if (isDataCloneNavigationError(err)) {
				setError(t("loginNavigationCloneError"));
				return;
			}
			setError(
				err.code === "GOOGLE_ONLY_ACCOUNT"
					? t("googleOnlyAccountError")
					: err.message,
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRegister = async (e) => {
		e.preventDefault();
		setError("");
		setRegisterError("");
		setRegisterFieldErrors({
			email: false,
			confirmEmail: false,
			password: false,
			confirmPassword: false,
		});

		if (
			registerData.email.trim().toLowerCase() !==
			registerData.confirmEmail.trim().toLowerCase()
		) {
			const message = t("emailsDoNotMatch");
			setError(message);
			setRegisterError(message);
			setRegisterFieldErrors((prev) => ({
				...prev,
				email: true,
				confirmEmail: true,
			}));
			toast.error(message);
			return;
		}

		if (registerData.password !== registerData.confirmPassword) {
			const message = t("passwordsDoNotMatch");
			setError(message);
			setRegisterError(message);
			setRegisterFieldErrors((prev) => ({
				...prev,
				password: true,
				confirmPassword: true,
			}));
			toast.error(message);
			return;
		}

		if (registerData.password.length < 6) {
			const message = t("passwordTooShort");
			setError(message);
			setRegisterError(message);
			setRegisterFieldErrors((prev) => ({
				...prev,
				password: true,
				confirmPassword: false,
			}));
			toast.error(message);
			return;
		}

		setIsLoading(true);

		try {
			const authUser = await register(
				registerData.name,
				registerData.email,
				registerData.password,
				null,
				null,
				registerData.surname,
			);
			await applyPendingInviteIfAny();
			if (shouldCompleteProfile(authUser)) {
				toast.info(t("completeProfileAfterGoogle"));
				navigateToProfileSetupSafe();
				return;
			}
			navigateToDashboardSafe();
		} catch (err) {
			if (isDataCloneNavigationError(err)) {
				const message = t("loginNavigationCloneError");
				setError(message);
				setRegisterError(message);
				toast.error(message);
				return;
			}
			const message =
				err.code === "GOOGLE_ONLY_ACCOUNT"
					? t("googleOnlyAccountError")
					: err.message;
			setError(message);
			setRegisterError(message);
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	// Native Google OAuth success handler
	const handleGoogleSuccess = async (credentialResponse) => {
		setError("");
		setIsLoading(true);

		try {
			const authUser = await loginWithGoogleCredential(
				credentialResponse.credential,
			);
			await applyPendingInviteIfAny();
			if (shouldCompleteProfile(authUser)) {
				toast.info(t("completeProfileAfterGoogle"));
				navigateToProfileSetupSafe();
				return;
			}
			navigateToDashboardSafe();
		} catch (err) {
			if (isDataCloneNavigationError(err)) {
				setError(t("loginNavigationCloneError"));
				return;
			}
			setError(err.message || t("googleLoginError"));
			toast.error(t("googleLoginError"));
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleError = () => {
		setError(t("googleLoginError"));
		toast.error(t("googleLoginError"));
	};

	// Shared form content rendered in both mobile and desktop
	const wrongOrigin = getWrongOrigin();

	const renderFormContent = (variant) => {
		const isMobile = variant === "mobile";
		return (
			<>
				{wrongOrigin && (
					<div
						className="mb-5 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200"
						data-testid="wrong-origin-warning"
					>
						<p className="font-semibold mb-1">
							Stai usando un indirizzo vecchio
						</p>
						<p className="mb-2 text-amber-200/80">
							Questa pagina è aperta da <code>{wrongOrigin}</code>, e l'accesso
							con Google da qui non funziona. Succede quando l'icona sulla
							schermata home è stata aggiunta quando l'app stava su un altro
							indirizzo.
						</p>
						<a
							href={CANONICAL_ORIGIN}
							className="inline-block rounded-sm bg-amber-400 px-3 py-1.5 font-semibold text-black"
						>
							Apri l'indirizzo corretto
						</a>
						<p className="mt-2 text-amber-200/60">
							Poi elimina la vecchia icona e riaggiungila alla schermata home da
							lì.
						</p>
					</div>
				)}

				{/* Native Google Login Button */}
				<div
					className={`flex justify-center ${isMobile ? "mb-5" : "mb-6"}`}
					data-testid="google-login-container"
				>
					<GoogleLogin
						onSuccess={handleGoogleSuccess}
						onError={handleGoogleError}
						useOneTap={false}
						theme="filled_black"
						size="large"
						width="100%"
						text="continue_with"
						shape="rectangular"
						locale="it"
					/>
				</div>

				<div className={`relative ${isMobile ? "mb-5" : "mb-6"}`}>
					<div className="absolute inset-0 flex items-center">
						<span className="w-full border-t border-border" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span
							className={`${isMobile ? "bg-[rgba(18,18,18,0.8)]" : "bg-card"} px-2 text-muted-foreground`}
						>
							{t("orWith")}
						</span>
					</div>
				</div>

				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList
						className={`grid w-full grid-cols-2 bg-secondary rounded-sm ${isMobile ? "mb-4" : "mb-6"}`}
					>
						<TabsTrigger
							value="login"
							className="rounded-sm data-[state=active]:bg-volt data-[state=active]:text-white"
						>
							{t("login")}
						</TabsTrigger>
						<TabsTrigger
							value="register"
							className="rounded-sm data-[state=active]:bg-volt data-[state=active]:text-white"
						>
							{t("register")}
						</TabsTrigger>
					</TabsList>

					{sessionExpired && (
						<div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-sm text-amber-400 text-sm flex items-center gap-2">
							<AlertCircle className="w-4 h-4" />
							{t("sessionExpired")}
						</div>
					)}

					{error && (
						<div className="mb-4 p-3 bg-blaze/10 border border-blaze/20 rounded-sm text-blaze text-sm">
							{error}
						</div>
					)}

					<TabsContent value="login">
						<form
							onSubmit={handleLogin}
							className={`space-y-${isMobile ? "3" : "4"}`}
						>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-login-email`}
									className="label-tactical"
								>
									Email
								</Label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-login-email`}
										type="email"
										placeholder="nome@email.com"
										value={loginData.email}
										onChange={(e) =>
											setLoginData({
												...loginData,
												email: e.target.value,
											})
										}
										className={`pl-10 bg-secondary border-transparent ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="login-email-input"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-login-password`}
									className="label-tactical"
								>
									Password
								</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-login-password`}
										type="password"
										placeholder="••••••••"
										value={loginData.password}
										onChange={(e) =>
											setLoginData({
												...loginData,
												password: e.target.value,
											})
										}
										className={`pl-10 bg-secondary border-transparent ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="login-password-input"
									/>
								</div>
							</div>
							<div className="flex justify-end -mt-1">
								<button
									type="button"
									onClick={handleForgotPassword}
									disabled={forgotLoading}
									className="text-xs text-muted-foreground hover:text-volt underline underline-offset-2 disabled:opacity-60"
									data-testid="forgot-password-link"
								>
									{forgotLoading ? t("sending") : t("forgotPasswordLink")}
								</button>
							</div>
							<Button
								type="submit"
								className={`w-full ${isMobile ? "h-11" : "h-12"} bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm`}
								disabled={isLoading}
								data-testid="login-submit-btn"
							>
								{isLoading ? t("loggingIn") : t("login")}
								<ChevronRight className="w-4 h-4 ml-2" />
							</Button>
						</form>
					</TabsContent>

					<TabsContent value="register">
						<form
							onSubmit={handleRegister}
							className={`space-y-${isMobile ? "3" : "4"}`}
						>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-name`}
									className="label-tactical"
								>
									{t("name")}
								</Label>
								<div className="relative">
									<User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-name`}
										type="text"
										placeholder={t("yourName")}
										value={registerData.name}
										onChange={(e) =>
											setRegisterData({
												...registerData,
												name: e.target.value,
											})
										}
										className={`pl-10 bg-secondary border-transparent ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="register-name-input"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-surname`}
									className="label-tactical"
								>
									{t("surname")}
								</Label>
								<div className="relative">
									<User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-surname`}
										type="text"
										placeholder={t("yourSurname")}
										value={registerData.surname}
										onChange={(e) =>
											setRegisterData({
												...registerData,
												surname: e.target.value,
											})
										}
										className={`pl-10 bg-secondary border-transparent ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										data-testid="register-surname-input"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-email`}
									className="label-tactical"
								>
									Email
								</Label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-email`}
										type="email"
										placeholder="nome@email.com"
										value={registerData.email}
										onChange={(e) => {
											setRegisterData({
												...registerData,
												email: e.target.value,
											});
											if (registerFieldErrors.email || registerError) {
												setRegisterFieldErrors((prev) => ({
													...prev,
													email: false,
													confirmEmail: false,
												}));
												setRegisterError("");
											}
										}}
										className={`pl-10 bg-secondary ${registerFieldErrors.email ? "border-blaze/40 ring-1 ring-blaze/40" : "border-transparent"} ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="register-email-input"
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-confirm-email`}
									className="label-tactical"
								>
									{t("confirmEmail")}
								</Label>
								<div className="relative">
									<Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-confirm-email`}
										type="email"
										placeholder="nome@email.com"
										value={registerData.confirmEmail}
										onChange={(e) => {
											setRegisterData({
												...registerData,
												confirmEmail: e.target.value,
											});
											if (registerFieldErrors.confirmEmail || registerError) {
												setRegisterFieldErrors((prev) => ({
													...prev,
													email: false,
													confirmEmail: false,
												}));
												setRegisterError("");
											}
										}}
										onPaste={(e) => e.preventDefault()}
										className={`pl-10 bg-secondary ${registerFieldErrors.confirmEmail ? "border-blaze/40 ring-1 ring-blaze/40" : "border-transparent"} ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="register-confirm-email-input"
									/>
								</div>
							</div>
							<p className="text-xs text-muted-foreground">
								{t("newAccountNote")}
							</p>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-password`}
									className="label-tactical"
								>
									Password
								</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-password`}
										type="password"
										placeholder="••••••••"
										value={registerData.password}
										className={`pl-10 bg-secondary ${registerFieldErrors.password ? "border-blaze/40 ring-1 ring-blaze/40" : "border-transparent"} ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="register-password-input"
										onChange={(e) => {
											setRegisterData({
												...registerData,
												password: e.target.value,
											});
											if (registerFieldErrors.password || registerError) {
												setRegisterFieldErrors((prev) => ({
													...prev,
													password: false,
												}));
												setRegisterError("");
											}
										}}
									/>
								</div>
							</div>
							<div className="space-y-2">
								<Label
									htmlFor={`${variant}-register-confirm`}
									className="label-tactical"
								>
									{t("confirmPassword")}
								</Label>
								<div className="relative">
									<Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										id={`${variant}-register-confirm`}
										type="password"
										placeholder="••••••••"
										value={registerData.confirmPassword}
										className={`pl-10 bg-secondary ${registerFieldErrors.confirmPassword ? "border-blaze/40 ring-1 ring-blaze/40" : "border-transparent"} ${isMobile ? "h-11" : "h-12"} rounded-sm`}
										required
										data-testid="register-confirm-input"
										onChange={(e) => {
											setRegisterData({
												...registerData,
												confirmPassword: e.target.value,
											});
											if (
												registerFieldErrors.confirmPassword ||
												registerError
											) {
												setRegisterFieldErrors((prev) => ({
													...prev,
													confirmPassword: false,
												}));
												setRegisterError("");
											}
										}}
									/>
								</div>
							</div>
							{registerError && (
								<div
									className="p-3 bg-blaze/10 border border-blaze/20 rounded-sm text-blaze text-sm"
									role="alert"
									aria-live="polite"
								>
									{registerError}
								</div>
							)}
							<Button
								type="submit"
								className={`w-full ${isMobile ? "h-11" : "h-12"} bg-volt hover:bg-volt/90 text-white font-bold uppercase tracking-wider rounded-sm`}
								disabled={isLoading}
								data-testid="register-submit-btn"
							>
								{isLoading ? t("registering") : t("register")}
								<ChevronRight className="w-4 h-4 ml-2" />
							</Button>
						</form>
					</TabsContent>
				</Tabs>
			</>
		);
	};

	return (
		<div className="theme-dark-scope login-shell bg-obsidian min-h-screen overflow-x-hidden lg:h-auto lg:overflow-visible relative">
			{/* Language toggle - top right, visible on all layouts */}
			<div className="login-language-toggle absolute top-4 right-4 z-50">
				<LanguageToggle />
			</div>
			{/* ===== DESKTOP LAYOUT (lg+) ===== */}
			<div className="hidden lg:flex min-h-screen">
				{/* Left side - Hero */}
				<div className="w-1/2 relative overflow-hidden">
					<div
						className="absolute inset-0 bg-cover bg-center"
						style={{
							backgroundImage: `url('https://images.unsplash.com/photo-1770513649465-2c60c8039806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxjcm9zc2ZpdCUyMGF0aGxldGUlMjBsaWZ0aW5nJTIwd2VpZ2h0cyUyMGRhcmslMjBneW18ZW58MHx8fHwxNzczMjI3NTIwfDA&ixlib=rb-4.1.0&q=85')`,
						}}
					/>
					<div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
					<div className="relative z-10 flex flex-col justify-center p-12">
						<div className="flex items-center gap-3 mb-8">
							<img
								src="/logo-kraveon.svg"
								alt="Kraveon"
								className="w-12 h-12 rounded-lg"
							/>
							<span className="font-heading text-2xl tracking-tight">
								KRAVEON
							</span>
						</div>
						<h1 className="font-heading text-5xl lg:text-6xl leading-tight mb-6">
							ALLENA.
							<br />
							TRACCIA.
							<br />
							<span className="text-volt">DOMINA.</span>
						</h1>
						<p className="text-muted-foreground text-lg max-w-md">
							{t("heroDescription")}
						</p>
					</div>
				</div>

				{/* Right side - Auth forms */}
				<div className="flex-1 flex justify-center overflow-y-auto p-12">
					<div className="w-full max-w-md my-auto py-6">
						<Card className="bg-card border-border rounded-sm">
							<CardHeader className="space-y-1 pb-4">
								<CardTitle className="font-heading text-2xl">
									{t("welcomeBack")}
								</CardTitle>
								<CardDescription>{t("loginSubtitle")}</CardDescription>
							</CardHeader>
							<CardContent>{renderFormContent("desktop")}</CardContent>
						</Card>
					</div>
				</div>
			</div>

			{/* ===== MOBILE LAYOUT (< lg) ===== */}
			<div className="login-mobile-viewport flex lg:hidden flex-col h-[100svh] min-h-[100svh] w-full relative overflow-hidden overflow-x-hidden">
				{/* Background image - optimized for mobile (smaller, lower quality) */}
				<div
					className="absolute inset-0 bg-cover bg-center"
					style={{
						backgroundImage: `url('https://images.pexels.com/photos/16513596/pexels-photo-16513596.jpeg?auto=compress&cs=tinysrgb&w=800&q=50')`,
					}}
				/>
				{/* Multi-layer gradient overlays for depth & readability */}
				<div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/40 to-obsidian" />
				<div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/70 to-transparent" />
				{/* Subtle side vignette */}
				<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(10,10,10,0.6)_100%)]" />

				{/* Subtle volt glow accent behind hero */}
				<div
					className="absolute top-8 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full opacity-[0.06] pointer-events-none"
					style={{
						background: "radial-gradient(circle, #00D68F 0%, transparent 70%)",
					}}
				/>

				{/* Hero Content */}
				<div className="login-hero-content relative z-10 flex flex-col items-center pt-6 pb-2 px-6 flex-shrink-0">
					{/* Logo */}
					<div className="flex items-center gap-3 mb-3">
						<img
							src="/logo-kraveon.svg"
							alt="Kraveon"
							className="w-10 h-10 rounded-lg"
						/>
						<span className="font-heading text-xl tracking-tight">KRAVEON</span>
					</div>

					{/* Tagline */}
					<h2 className="font-heading text-2xl leading-tight text-center tracking-tight mb-1">
						ALLENA. TRACCIA. <span className="text-volt">DOMINA.</span>
					</h2>
					<p className="text-muted-foreground text-xs text-center max-w-[250px]">
						{t("heroDescriptionShort")}
					</p>

					{/* Accent line */}
					<div className="mt-3 w-10 h-[2px] rounded-full bg-volt/30" />
				</div>

				{/* Form Section - Glass card with internal scroll for long forms */}
				<div className="login-mobile-form-wrap relative z-10 px-4 pb-4 pt-4 flex-1 min-h-0 flex flex-col justify-center">
					<div className="bg-card/75 backdrop-blur-2xl border border-white/[0.05] rounded-sm shadow-[0_-4px_30px_-8px_rgba(0,214,143,0.06)] flex flex-col min-h-0">
						{/* Card Header */}
						<div className="px-5 pt-4 pb-2">
							<h3 className="font-heading text-lg text-white">
								{t("welcomeBack")}
							</h3>
							<p className="text-xs text-muted-foreground mt-0.5">
								{t("loginSubtitle")}
							</p>
						</div>
						{/* Card Content */}
						<div className="login-mobile-form-scroll px-5 pb-4 overflow-y-auto min-h-0">
							{renderFormContent("mobile")}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
